<template>
    <Dialog 
        v-model:visible="visible" 
        modal
        :header="`Payer Information - ${payer?.advertiserName || 'Unknown Advertiser'}`"
        class="w-[90vw] md:w-[70vw] lg:w-[50vw]"
    >
        <div v-if="payer">
            <div class="flex justify-end mb-4">
                <Button 
                    icon="pi pi-copy" 
                    label="Copy All" 
                    severity="secondary"
                    outlined
                    text 
                    v-tooltip.bottom="'Copy all payer information'"
                    @click="copyPayerData" 
                />
            </div>
            
            <!-- Show structured data if address exists -->
            <div v-if="payer.address" class="grid grid-cols-2 gap-4">
                <div class="flex flex-col">
                    <span class="font-bold">Name:</span>
                    <span>{{ payer.name }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">Payment Method:</span>
                    <span>{{ getPaymentMethodName(payer.paymentMethod) }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">Threshold:</span>
                    <span>€{{ payer.threshold }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">Payment Provider:</span>
                    <span>{{ payer.paymentProvider }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">Contact:</span>
                    <span>{{ payer.contact }}</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">VAT:</span>
                    <span>{{ payer.vat || 'N/A' }}</span>
                </div>
                <div class="flex flex-col col-span-2">
                    <span class="font-bold">Address:</span>
                    <span>{{ payer.address }}, {{ payer.city }}, {{ payer.country }}</span>
                </div>
                <div class="flex flex-col col-span-2">
                    <span class="font-bold">Note:</span>
                    <span>{{ payer.note || 'No notes' }}</span>
                </div>
            </div>
            
            <!-- Show unstructured data if address is empty -->
            <div v-else class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div class="flex flex-col">
                        <span class="font-bold">Payment Method:</span>
                        <span>{{ getPaymentMethodName(payer.paymentMethod) }}</span>
                    </div>
                    <div class="flex flex-col">
                        <span class="font-bold">Threshold:</span>
                        <span>€{{ payer.threshold }}</span>
                    </div>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold">Payer Details:</span>
                    <div class="mt-2 p-3 bg-gray-500 rounded border">
                        <pre class="whitespace-pre-wrap text-sm text-white">{{ payer.unstructuredPayerDetails }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </Dialog>
</template>

<script setup>
import { computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { getPaymentMethodName } from '@/utils/paymentMethods';

const props = defineProps({
    show: {
        type: Boolean,
        required: true
    },
    payer: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['update:show']);

const toast = useToast();

const visible = computed({
    get: () => props.show,
    set: (value) => emit('update:show', value)
});

const copyPayerData = async () => {
    try {
        let payerInfo;
        
        if (props.payer.address) {
            // Structured data
            payerInfo = `
Payer Information for ${props.payer.advertiserName}
----------------------------------------
Name: ${props.payer.name}
Payment Method: ${getPaymentMethodName(props.payer.paymentMethod)}
Threshold: €${props.payer.threshold}
Payment Provider: ${props.payer.paymentProvider || 'N/A'}
Contact: ${props.payer.contact || 'N/A'}
VAT: ${props.payer.vat || 'N/A'}
Address: ${props.payer.address || 'N/A'}, ${props.payer.city || 'N/A'}, ${props.payer.country || 'N/A'}
Note: ${props.payer.note || 'No notes'}
`.trim();
        } else {
            // Unstructured data
            payerInfo = `
Payer Information for ${props.payer.advertiserName}
----------------------------------------
Payment Method: ${getPaymentMethodName(props.payer.paymentMethod)}
Threshold: €${props.payer.threshold}
Payer Details:
${props.payer.unstructuredPayerDetails}
`.trim();
        }

        await navigator.clipboard.writeText(payerInfo);
        toast.add({
            severity: 'success',
            summary: 'Copied!',
            detail: 'All payer information copied to clipboard',
            life: 3000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy to clipboard',
            life: 3000
        });
    }
};
</script>